/**
 * Panel derecho de la sesión de llamada (CRM / pedido). Solo se usa dentro de
 * `CallCenterPhoneActiveSession` — no montar directamente en otras pantallas.
 *
 * La lógica de datos vive en hooks aislados (useCallCenterOrderContext +
 * useLeadByPhone) para mantener este componente como render-only.
 */

import { BlockStack, Box, Button, InlineStack, Text } from '@/lib/polaris';
import { formatSecondsAsClock } from '@/lib/formatClockDuration';
import { ROUTES } from '@/config/routes.config';
import { useStoreNavigate } from '@/hooks/useStoreNavigate';
import { isPstnAnsweredForUi } from '@/lib/callCenterPstnConversation';
import type { WebrtcCallUiPhase } from '@/lib/telnyxCallState';
import { AgentQuickOrderPanel } from './AgentQuickOrderPanel';
import { AgentWaQuickActions } from './AgentWaQuickActions';
import { useCallCenterOrderContext, type OrderContext } from './useCallCenterOrderContext';
import { useLeadByPhone, type LeadMatch } from './useLeadByPhone';

export type CallDetailsColumnVariant = 'floating' | 'fab';

export interface CallCenterActiveCallRightColumnProps {
  tenantId: string;
  crmCallId?: string | null;
  crmCallStatus?: string | null;
  remotePhone: string | null;
  callPhase: WebrtcCallUiPhase;
  callDirection: 'inbound' | 'outbound' | null;
  elapsedSeconds: number;
  onOpenDialer: () => void;
  variant: CallDetailsColumnVariant;
}

/** Resuelve el nombre a mostrar con cascada de fallbacks: lead → orderHint → detail split → genérico. */
function resolveDisplayName(lead: LeadMatch | null, orderContext: OrderContext | null): string {
  if (lead?.name?.trim()) return lead.name.trim();
  if (orderContext?.nameHint?.trim()) return orderContext.nameHint.trim();
  if (orderContext?.kind === 'cod') {
    const split = orderContext.detail.split(' — ')[0]?.trim();
    if (split) return split;
  }
  if (orderContext?.kind === 'ecommerce') {
    const split = orderContext.detail.split(' · ')[0]?.trim();
    if (split) return split;
  }
  return 'Sin nombre en CRM';
}

function resolveTitle(
  effectiveInCall: boolean,
  callDirection: 'inbound' | 'outbound' | null,
): string {
  if (effectiveInCall) return 'En llamada';
  return callDirection === 'inbound' ? 'Llamada entrante' : 'Timbrando';
}

export function CallCenterActiveCallRightColumn({
  tenantId,
  crmCallId = null,
  crmCallStatus = null,
  remotePhone,
  callPhase,
  callDirection,
  elapsedSeconds,
  onOpenDialer,
  variant,
}: CallCenterActiveCallRightColumnProps) {
  const storeNavigate = useStoreNavigate();
  const orderContext = useCallCenterOrderContext(tenantId, crmCallId, crmCallStatus, callPhase);
  const lead = useLeadByPhone(tenantId, remotePhone);

  const isFab = variant === 'fab';
  const pstnAnswered = isPstnAnsweredForUi(crmCallId, crmCallStatus);
  const effectiveRinging =
    callPhase === 'ringing' || (callPhase === 'active' && !pstnAnswered);
  const effectiveInCall = callPhase === 'active' && pstnAnswered;
  const timerLabel = formatSecondsAsClock(elapsedSeconds);
  const title = resolveTitle(effectiveInCall, callDirection);
  const displayName = resolveDisplayName(lead, orderContext);
  const displayPhone =
    remotePhone?.trim() || orderContext?.phoneFromOrder?.trim() || '—';
  const showQuickActions = Boolean(tenantId && remotePhone && pstnAnswered);
  const quickActionsPhone = orderContext?.phoneFromOrder ?? remotePhone ?? '';
  const quickActionsName = lead?.name ?? orderContext?.nameHint ?? null;

  return (
    <Box
      background="bg-surface"
      padding={isFab ? '500' : '400'}
      width={isFab ? '100%' : undefined}
      minHeight={isFab ? 'min-content' : undefined}
    >
      <BlockStack gap={isFab ? '300' : '200'}>
        {isFab ? (
          <Text as="h3" variant="headingSm">
            Contacto / pedido
          </Text>
        ) : (
          <>
            <Text as="h2" variant="headingSm">
              Llamada
            </Text>
            <InlineStack gap="200" blockAlign="center" wrap={false}>
              <Text as="span" variant="bodyMd" fontWeight="semibold">
                {title}
              </Text>
              {effectiveRinging ? (
                <span className="sf-call-status-dots" aria-hidden>
                  <span className="sf-call-status-dot" />
                  <span className="sf-call-status-dot" />
                  <span className="sf-call-status-dot" />
                </span>
              ) : null}
              {effectiveInCall && (
                <Text as="span" variant="bodyMd" tone="subdued">
                  {timerLabel}
                </Text>
              )}
            </InlineStack>
          </>
        )}

        {lead?.id ? (
          <Button
            variant="plain"
            textAlign="left"
            onClick={() => storeNavigate(ROUTES.APPS_CRM_LEAD_DETAIL.replace(':id', lead.id))}
          >
            {displayName}
          </Button>
        ) : (
          <Text as="p" variant={isFab ? 'headingSm' : 'bodyMd'}>
            {displayName}
          </Text>
        )}

        <Text as="p" variant={isFab ? 'bodyLg' : 'bodySm'} tone="subdued">
          {displayPhone}
        </Text>

        {orderContext ? (
          <OrderContextBlock orderContext={orderContext} onNavigate={storeNavigate} />
        ) : null}

        {!isFab && callDirection ? (
          <Text as="p" variant="bodyMd" tone="subdued">
            {callDirection === 'inbound' ? 'Entrante' : 'Saliente'}
          </Text>
        ) : null}

        <InlineStack gap="200">
          <Button size={isFab ? 'medium' : 'slim'} onClick={onOpenDialer}>
            {isFab ? 'Marcador en página completa' : 'Ir al Marcador'}
          </Button>
        </InlineStack>

        {/* Voice Commerce M3 + M4: mini-POS + WhatsApp quick actions.
            Solo visible cuando la llamada está efectivamente conectada (PSTN
            answered) y hay teléfono del cliente. */}
        {showQuickActions && (
          <>
            <AgentQuickOrderPanel
              tenantId={tenantId}
              customerPhone={quickActionsPhone}
              customerName={quickActionsName}
              crmCallId={crmCallId ?? null}
              compact={isFab}
            />
            <AgentWaQuickActions
              tenantId={tenantId}
              customerPhone={quickActionsPhone}
              customerName={quickActionsName}
              crmCallId={crmCallId ?? null}
              compact={isFab}
            />
          </>
        )}
      </BlockStack>
    </Box>
  );
}

function OrderContextBlock({
  orderContext,
  onNavigate,
}: {
  orderContext: OrderContext;
  onNavigate: (path: string) => void;
}) {
  if (orderContext.link) {
    return (
      <Button variant="plain" textAlign="left" onClick={() => onNavigate(orderContext.link!)}>
        <BlockStack gap="050">
          <Text as="span" variant="bodySm" tone="subdued">
            {orderContext.label}
          </Text>
          <Text as="span" variant="bodyMd">
            {orderContext.detail}
          </Text>
        </BlockStack>
      </Button>
    );
  }
  return (
    <BlockStack gap="100">
      <Text as="p" variant="bodySm" tone="subdued">
        {orderContext.label}
      </Text>
      <Text as="p" variant="bodyMd">
        {orderContext.detail}
      </Text>
    </BlockStack>
  );
}
